import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Add, Delete, Edit, Help, Star, StarBorder } from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
    HtmlTooltip,
    HtmlTooltipDisabled,
    StyledDarkRedInformationTypography,
} from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { DisabledMutedGreyButton } from "../../../components/commonComponents/Buttons/DisabledButton";
import {
    ListingCellCenter,
    ListingCellLeft,
    ListingCellRight,
    ListingHeaderContent,
} from "../../../components/commonComponents/TabularLists/styles";
import CompanyIconCount from "../../../components/commonComponents/TabularLists/CompanyIconCount";
import {
    canAssignOrMarkFavorite,
    checkUserDetailsFromCookie,
    checkUserPermissions,
    deleteUser,
    deleteUserFromSpecificCompany,
    manageUsersPaginationCookie,
    markFavoriteUser,
    normalizeUserListType,
    resetUserData,
    userListFilterCookieKey,
    userListStateKey,
} from "../../../api_calls/userManagement";
import {
    getBottomNavRetailerSelectionFromCookies,
    isLockedOutsideAllRetailers,
    resolveRetailerSelectionOptions,
} from "../../../api_calls/retailerSelection";
import { tenantRetailerScopeAllowsOperation } from "../../../api_calls/tenantRetailerRecordAccess";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import Pagination from "../../../components/commonComponents/LoadMore/Pagination";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import AddBrandUser from "../modalContent/AddBrandUser";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";
import ExpandableNameList from "../../../components/commonComponents/TabularLists/ExpandableNameList";
import { SortIcon } from "../../../api_calls/utils";

// Tooltip shown on Add / Edit / Delete when a specific retailer (not All Retailers) is selected
const ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP = "Only in All Retailers";

const invitedByLabelsForUser = (user) => {
    const companies = Array.isArray(user.assigned_companies) ? user.assigned_companies : [];
    const fromCompanies = companies
        .map((company) => (typeof company.invited_by === "string" && company.invited_by !== "" ? company.invited_by : null))
        .filter((value) => value !== null);
    if (fromCompanies.length > 0) {
        return fromCompanies;
    }
    if (typeof user.invited_by === "string" && user.invited_by !== "") {
        return [user.invited_by];
    }
    return [];
};

const invitedBySearchText = (user) => invitedByLabelsForUser(user).join(", ").toLowerCase();

const invitedBySortKey = (user) => invitedByLabelsForUser(user).sort().join(", ").toLowerCase();

const BrandUserlist = ({ usedFor }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [modalDetails, setModalDetails] = React.useState({});
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [userToDelete, setUserToDelete] = React.useState({});
    const [fieldError, setFieldError] = React.useState({});
    const [showMultiCompanyConfirmation, setShowMultiCompanyConfirmation] = React.useState(false);

    const [userToDeleteFromCompany, setUserToDeleteFromCompany] = React.useState({});
    const [searchVal, setSearchVal] = React.useState({ value: "" });
    const allowedAccessRights = checkUserPermissions();
    const permissionScope = allowedAccessRights.allowed_access;

    const checkUserDetails = checkUserDetailsFromCookie();
    const companyAllowedUserTypes = ["cyreen", "branch"];
    const loggedInUserType =
        checkUserDetails.success === true && typeof checkUserDetails.user_detail?.user_type === "string"
            ? checkUserDetails.user_detail.user_type.toLowerCase()
            : "";
    const showCompanyColumn = companyAllowedUserTypes.includes(loggedInUserType);
    const showInvitedByColumn = showCompanyColumn !== true;
    // Brands list groups by company assignment, so Retailer is not shown here.
    const normalizedUserType = normalizeUserListType(usedFor);
    const stateKey = userListStateKey(normalizedUserType);
    const filterCookieKeyword = userListFilterCookieKey(normalizedUserType);

    const currentStateData = useSelector((state) => state.userSelections);

    // Prefer Redux bottom-nav selection so the lock stays in sync; fall back to cookie
    const bottomNavRetailerSelection =
        typeof currentStateData.bottom_nav_selected_retailer !== "undefined"
            ? currentStateData.bottom_nav_selected_retailer
            : getBottomNavRetailerSelectionFromCookies();
    // Lock only if the user can switch retailers and did not pick All Retailers.
    // One company (static name, no dropdown) stays unlocked.
    const isAccountManagementLocked = isLockedOutsideAllRetailers(
        bottomNavRetailerSelection,
        resolveRetailerSelectionOptions(currentStateData)
    );

    // sorting measures for files listing
    const [sortingMeasure, setSortingMeasure] = React.useState({
        username: "asc",
        email: "asc",
        company: "asc",
        invited_by: "asc",
    });

    const [sortingParamName, setSortingParamName] = React.useState("username");

    const paginationStartValue =
        typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_start : 0;
    const paginationEndValue =
        typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_end : 10;

    const userRecords = typeof currentStateData[stateKey] !== "undefined" && currentStateData[stateKey].length !== 0 ? currentStateData[stateKey] : [];

    const applyFilter = () => {
        if (userRecords.length !== 0) {
            createCookie(filterCookieKeyword, JSON.stringify(searchVal.value));

            return sortedResults(
                userRecords.filter((user) => {
                    const username = typeof user.username !== "undefined" && user.username !== null ? user.username : "";
                    const email = typeof user.email !== "undefined" && user.email !== null ? user.email : "";
                    const companyDetails = Array.isArray(user.assigned_companies) ? user.assigned_companies : [];
                    const roles = Array.isArray(user.roles) ? user.roles : [];
                    const searchLower = searchVal.value.toLowerCase().toString();
                    // retailer roots shown in the Retailer column are searchable too
                    const retailerNames = (Array.isArray(user.retailers) ? user.retailers : [])
                        .map((retailer) => retailer.name)
                        .join(", ");

                    return (
                        username.toLowerCase().includes(searchLower) ||
                        email.toLowerCase().includes(searchLower) ||
                        (showInvitedByColumn === true && invitedBySearchText(user).includes(searchLower)) ||
                        (showCompanyColumn === true &&
                            companyDetails.some(
                                (company) =>
                                    (typeof company.name === "string" ? company.name : "")
                                        .toLowerCase()
                                        .includes(searchLower) ||
                                    (typeof company.invited_by === "string" ? company.invited_by : "")
                                        .toLowerCase()
                                        .includes(searchLower)
                            )) ||
                        retailerNames.toLowerCase().includes(searchLower) ||
                        roles.join(",").toLowerCase().includes(searchLower)
                    );
                })
            );
        } else {
            return [];
        }
    };

    // to return the sorted list items
    const sortedResults = (toBeSortedList) => {
        return toBeSortedList.slice().sort((a, b) => {
            const firstEmail = typeof a.email !== "undefined" && a.email !== null ? a.email : "";
            const secondEmail = typeof b.email !== "undefined" && b.email !== null ? b.email : "";
            const firstInvitedBy = invitedBySortKey(a);
            const secondInvitedBy = invitedBySortKey(b);

            if (sortingParamName === "invited_by") {
                return sortingMeasure.invited_by === "asc"
                    ? firstInvitedBy.localeCompare(secondInvitedBy)
                    : secondInvitedBy.localeCompare(firstInvitedBy);
            } else if (sortingParamName === "email") {
                return sortingMeasure.email === "asc" ? firstEmail.localeCompare(secondEmail) : secondEmail.localeCompare(firstEmail);
            } else if (sortingParamName === "company") {
                const firstCompanyDetails = Array.isArray(a.assigned_companies) ? a.assigned_companies : [];
                const secondCompanyDetails = Array.isArray(b.assigned_companies) ? b.assigned_companies : [];
                const aCompanyNames = firstCompanyDetails.map((company) => company.name).sort().join(", ");
                const bCompanyNames = secondCompanyDetails.map((company) => company.name).sort().join(", ");
                return sortingMeasure.company === "asc"
                    ? aCompanyNames.localeCompare(bCompanyNames)
                    : bCompanyNames.localeCompare(aCompanyNames);
                // return sortingMeasure.company === "asc"
                //     ? a.assigned_company.name.localeCompare(b.assigned_company.name)
                //     : b.assigned_company.name.localeCompare(a.assigned_company.name);
            } else {
                return sortingMeasure.email === "asc"
                    ? firstEmail.localeCompare(secondEmail)
                    : secondEmail.localeCompare(firstEmail);
            }
        });
    };

    let listItems = typeof searchVal.value !== "undefined" && searchVal.value !== "" ? applyFilter() : userRecords;

    let userListitems =
        listItems.length !== 0 ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue) : [];

    const closeModal = () => {
        setModalDetails({});
        setIsModalOpen(false);
        resetUserData();
    };

    const openModal = (modalFor, userData) => {
        setModalDetails({
            openFor: modalFor,
            user_data: userData,
        });
        setIsModalOpen(true);
    };

    const editAction = (user) => {
        return (
            <HtmlTooltip title={<Typography variant="body4">Edit</Typography>}>
                <a
                    href="javacript:void(0)"
                    aria-label={"edit user for " + user.username}
                    onClick={(e) => {
                        e.preventDefault();
                        openModal("update", user);
                    }}
                >
                    <Edit sx={{ color: "#AF3241" }} />
                </a>
            </HtmlTooltip>
        );
    };

    const deleteAction = (user) => {
        return (
            <HtmlTooltip title={<Typography variant="body4">Delete</Typography>}>
                <a
                    href="javacript:void(0)"
                    aria-label={"delete user for " + user.username}
                    onClick={(e) => {
                        e.preventDefault();
                        if (user.multiple_accounts === true) {
                            setShowMultiCompanyConfirmation(true);
                        } else {
                            setShowConfirmation(true);
                        }
                        setUserToDelete(user);
                    }}
                >
                    <Delete sx={{ color: "#AF3241" }} />
                </a>
            </HtmlTooltip>
        );
    };

    // tooltipTitle defaults to "No Access"; retailer lock passes ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP
    const disabledEditAction = (user, tooltipTitle = "No Access") => {
        return (
            <HtmlTooltipDisabled title={tooltipTitle}>
                <a
                    href="javacript:void(0)"
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                >
                    <Edit sx={{ color: "#E6E6E6" }} />
                </a>
            </HtmlTooltipDisabled>
        );
    };

    const disabledDeleteAction = (user, tooltipTitle = "No Access") => {
        return (
            <HtmlTooltipDisabled title={tooltipTitle}>
                <a
                    href="javacript:void(0)"
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                >
                    <Delete sx={{ color: "#E6E6E6" }} />
                </a>
            </HtmlTooltipDisabled>
        );
    };

    const handleSortingParamChange = (paramName) => {
        setSortingMeasure((prevState) => {
            return {
                ...prevState,
                [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
            };
        });
        setSortingParamName(paramName);
    };

    // user.retailers is built from each assigned company's retailer root.
    const tenantMayOperateOnUser = (user) =>
        tenantRetailerScopeAllowsOperation(
            loggedInUserType,
            (Array.isArray(user.retailers) ? user.retailers : []).map((retailer) => retailer.name),
            currentStateData
        );

    const columns = React.useMemo(() => {
        let cols = [
            {
                id: "Favorite",
                // headerAlign: "center",
                // align: "center",
                header: (
                    <ListingHeaderContent>
                        Favorite
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellLeft>
                        {/* Default star size, same as the action icons in this table. */}
                        {
                            canAssignOrMarkFavorite(user) ?
                                user.favourited === true
                                    ?
                                    <Star
                                        onClick={() => {
                                            markFavoriteUser(user.email, normalizedUserType).then((info) => {
                                                if (info.success === true) {
                                                    toast("User removed from favorites", { type: "success" });
                                                } else {
                                                    toast("Sorry, something went wrong", { type: "error" });
                                                }
                                            });
                                        }}
                                        sx={{ color: "#AF3241", cursor: "pointer" }}
                                    />
                                    :
                                    <StarBorder
                                        onClick={() => {
                                            markFavoriteUser(user.email, normalizedUserType).then((info) => {
                                                if (info.success === true) {
                                                    toast("User marked as favorite", { type: "success" });
                                                } else {
                                                    toast("Sorry, something went wrong", { type: "error" });
                                                }
                                            });
                                        }}
                                        sx={{ color: "#AF3241", cursor: "pointer" }}
                                    />
                                :
                                <StarBorder
                                    sx={{ color: "#62626E", cursor: "not-allowed" }}
                                />
                        }
                    </ListingCellLeft>
                ),
            },
            {
                id: "email",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("email")} style={{ cursor: "pointer" }}>
                        E-mail  <SortIcon direction={sortingMeasure.email} />
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellLeft>
                        {user.email}
                    </ListingCellLeft>
                ),
            }
        ];

        if (showCompanyColumn === true) {
            cols.push({
                id: "company",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingHeaderContent>
                        Company
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellCenter>
                        <CompanyIconCount
                            companies={Array.isArray(user.assigned_companies) ? user.assigned_companies : []}
                        />
                    </ListingCellCenter>
                ),
            });
        } else if (showInvitedByColumn === true) {
            cols.push({
                id: "invited_by",
                cellClassName: "wrapLargeText",
                cellSx: { maxWidth: 180 },
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("invited_by")} style={{ cursor: "pointer" }}>
                        Invited by <SortIcon direction={sortingMeasure.invited_by} />
                    </ListingHeaderContent>
                ),
                render: (user) => {
                    const companies = Array.isArray(user.assigned_companies) ? user.assigned_companies : [];
                    if (companies.length > 1) {
                        return (
                            <ListingCellLeft>
                                <ExpandableNameList
                                    items={companies.map((company, index) => ({
                                        key: company.id + "_invited_" + index,
                                        label: company?.invited_by ?? "N/A",
                                        tooltip: company?.name ?? "",
                                    }))}
                                />
                            </ListingCellLeft>
                        );
                    }
                    const singleInvitedBy =
                        companies.length === 1 && typeof companies[0].invited_by === "string"
                            ? companies[0].invited_by
                            : typeof user.invited_by !== "undefined" && user.invited_by !== null
                              ? user.invited_by
                              : "";
                    return <ListingCellLeft>{singleInvitedBy}</ListingCellLeft>;
                },
            });
        }

        cols = [
            ...cols,
            {
                id: "actions",
                headerAlign: "right",
                align: "right",
                header: (
                    <ListingHeaderContent>
                        Action
                    </ListingHeaderContent>
                ),
                render: (user) => (
                    <ListingCellRight>
                        {/* Specific retailer selected → always disable with All Retailers tooltip */}
                        {isAccountManagementLocked === true ? (
                            <>
                                {disabledEditAction(user, ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP)}
                                {disabledDeleteAction(user, ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP)}
                            </>
                        ) : (
                            <>
                                {/* Permission, then the tenant retailer check. A miss still shows the grey button. */}
                                {permissionScope.user_list_can_edit_brand === true && tenantMayOperateOnUser(user) === true
                                    ? editAction(user)
                                    : disabledEditAction(user)}
                                {permissionScope.user_list_can_delete_brand === true && tenantMayOperateOnUser(user) === true
                                    ? deleteAction(user)
                                    : disabledDeleteAction(user)}
                            </>
                        )}
                    </ListingCellRight>
                ),
            }
        ]

        return cols;
    }, [sortingMeasure, normalizedUserType, isAccountManagementLocked, showCompanyColumn, showInvitedByColumn, loggedInUserType, currentStateData]);

    React.useEffect(() => {
        // check if filter cookie is set and set it in redux state
        if (typeof document.cookie.split("; ").find((row) => row.startsWith(filterCookieKeyword)) !== "undefined") {
            const filterObj = JSON.parse(
                document.cookie
                    .split("; ")
                    .find((row) => row.startsWith(filterCookieKeyword))
                    .split("=")[1]
            );
            setSearchVal({ value: filterObj });
        }

        // reset pagination redux state
        manageUsersPaginationCookie({
            user_list_start: 0,
            user_list_end: 10,
        });

    }, [filterCookieKeyword, usedFor]);

    return (
        <>
            {/* Page-level notice when account management is locked to All Retailers view */}
            {isAccountManagementLocked === true && (
                <>
                    <StyledDarkRedInformationTypography variant="body2">
                        Accounts with access to the currently selected retailer are displayed for reference only. Account
                        management is only available in the “All Retailers” view.
                    </StyledDarkRedInformationTypography>
                    <Box height={20} />
                </>
            )}

            {/* ============ Action buttons =========== */}
            <Stack direction="row" spacing={2}>
                {isAccountManagementLocked === true ? (
                    // Muted grey stand-in so the locked action keeps the same footprint as the live button
                    <DisabledMutedGreyButton
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                        label={"New Brand User"}
                        icon={<Add />}
                        toolTipContent={ACCOUNT_MANAGEMENT_ALL_RETAILERS_TOOLTIP}
                        sx={{ mt: 3, mb: 2 }}
                    />
                ) : (
                    <ActionButton
                        onClick={(e) => {
                            e.preventDefault();
                            openModal("add", {});
                        }}
                        label={"New Brand User"}
                        icon={<Add />}
                    />
                )}
            </Stack>
            <Box height={30} />
            <Box width={350}>
                <CustomTextInput
                    fullWidth
                    variant="standard"
                    placeholder="Search"
                    label="Search"
                    autoComplete="off"
                    type={"text"}
                    value={searchVal.value}
                    onChange={(e) => {
                        const nextSearchValue = e.target.value;
                        if (nextSearchValue === "") {
                            deleteCookie(filterCookieKeyword, true);
                        }
                        setSearchVal({ value: nextSearchValue });
                        // searching changes the filtered set — always show results from page 1
                        const pageSize =
                            paginationEndValue > paginationStartValue
                                ? paginationEndValue - paginationStartValue
                                : 10;
                        manageUsersPaginationCookie({
                            user_list_start: 0,
                            user_list_end: pageSize,
                        });
                    }}
                    onBlur={(e) => { }}
                />
            </Box>

            {/* ============== users listing ============ */}
            <Box height={30} />
            <ListingDataTable
                columns={columns}
                rows={userListitems}
                rowKey={(user) => user.id}
                emptyText="No users found"
                loading={typeof currentStateData[stateKey] === "undefined" && userRecords.length === 0 ? true : false}
            />
            {/* --------- record count info and pagination --------- */}
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2} mb={5}>
                <Pagination
                    totalCount={listItems.length}
                    reduxPaginationKeyword="users_pagination"
                    reduxStartKeyword="user_list_start"
                    reduxEndKeyword="user_list_end"
                    useWindowScroll={true}
                />
            </Stack>


            {/* ============ modal popup for add/update User ========== */}
            {isModalOpen && (
                <AddBrandUser
                    open={isModalOpen}
                    onClose={closeModal}
                    size={"md"}
                    closeModal={closeModal}
                    usedFor={modalDetails.openFor}
                    userInfo={modalDetails.user_data}
                    cyreenBoolean={checkUserDetails?.user_detail?.user_type === "cyreen" ? true : false}
                    refreshUserType={normalizedUserType}
                />
            )}
            {/* ---------- delete Confirmation ---------- */}
            {showConfirmation === true && (
                <Confirmation
                    confirmationActionCall={() => {
                        deleteUser(userToDelete, normalizedUserType).then((info) => {
                            if (info.success === true) {
                                setUserToDelete({});
                                setShowConfirmation(false);
                                toast("User deleted successfully ", { type: "success" });
                            } else {
                                toast("Sorry, something went wrong", { type: "error" });
                            }
                        });
                    }}
                    closePopup={(e) => {
                        setUserToDelete({});
                        setShowConfirmation(false);
                    }}
                    showConfirmationPopup={showConfirmation}
                    confirmationTitle={"Delete Brand: " + userToDelete.username}
                    confirmationMessage={
                        <>
                            <Typography variant="body2">
                                You are deleting a brand account. This action is permanent and cannot be reversed.
                            </Typography>
                            <Box height={20} />
                            <Typography variant="body2">Are you sure you want to proceed?</Typography>
                        </>
                    }
                />
            )}
            {/* ------------ delete confirmation for user with multiple companies ------ */}
            {showMultiCompanyConfirmation === true && (
                <Confirmation
                    confirmationActionCall={() => {
                        setFieldError({});
                        if (typeof userToDeleteFromCompany.id === "undefined") {
                            setFieldError({ ...fieldError, user_company: "Please select a company" });
                            return;
                        } else {
                            deleteUserFromSpecificCompany(userToDelete, userToDeleteFromCompany.id, normalizedUserType).then((info) => {
                                if (info.success === true) {
                                    setUserToDelete({});
                                    setShowMultiCompanyConfirmation(false);
                                    setUserToDeleteFromCompany({});
                                    setFieldError({});
                                    toast("User deleted successfully", { type: "success" });
                                } else {
                                    toast("Sorry, something went wrong", { type: "error" });
                                }
                            });
                        }

                    }}
                    closePopupOnClose={false}
                    closePopup={(e) => {
                        setUserToDelete({});
                        setShowMultiCompanyConfirmation(false);
                        setUserToDeleteFromCompany({});
                        setFieldError({});
                    }}
                    showConfirmationPopup={showMultiCompanyConfirmation}
                    confirmationTitle={"Delete Brand: " + userToDelete.username}
                    confirmationMessage={
                        <>
                            <SingleSelect
                                enableFullWidth={true}
                                label={<span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    Company Selection* <HtmlTooltip title={
                                        <Box width={200}>
                                            <Typography variant="body2">
                                                Delete brand accounts for your company or any sub-company.
                                                Select the company for which you want to delete this brand account.
                                            </Typography>
                                        </Box>
                                    }>
                                        <Help fontSize="small" />
                                    </HtmlTooltip>
                                </span>}
                                selectionCheck={(option, value) => option.label === value.label}
                                sortOptions={false}
                                options={
                                    typeof userToDelete.assigned_companies !== "undefined" ? userToDelete.assigned_companies.map(companyDtl => {
                                        return { id: companyDtl.id, label: companyDtl.name };
                                    }) : []
                                }
                                defaultVal={userToDeleteFromCompany}
                                onChangeCall={(selectedOption) => {
                                    setUserToDeleteFromCompany(selectedOption);
                                }}
                                fieldError={
                                    typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                                        ? fieldError.user_company
                                        : ""
                                }
                            />
                            <Box height={20} />
                            <Typography variant="body2">
                                You are deleting a brand account. This action is permanent and cannot be reversed.
                            </Typography>
                            <Box height={20} />
                            <Typography variant="body2">Are you sure you want to proceed?</Typography>
                        </>
                    }
                />
            )}
        </>
    );
};

export default BrandUserlist;
